import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core'
import { AngularFireMessaging } from '@angular/fire/compat/messaging'
import { BehaviorSubject } from 'rxjs'
import { AuthenticationService } from '../authentication.service'
import { HttpClient } from '@angular/common/http'
import { NotificationService } from '../notification.service'
import { Services } from '../services'

@Injectable({
    providedIn: 'root',
})
export class DeviceNotificationService {
    private afMessaging = inject(AngularFireMessaging)
    private authService = inject(AuthenticationService)
    private notificationService = inject(NotificationService)
    private http = inject(HttpClient)

    token: WritableSignal<string | null> = signal(null)
    showInstructions = signal(false)
    isIOS = signal(true)

    currentMessage = new BehaviorSubject<any>(null)
    currentmessage$ = this.currentMessage.asObservable()

    constructor(private services: Services) {
        // Whenever the token and userId changes, we see if we have enough information to save the token.
        effect(() => {
            if (this.token() && this.authService.userId()) {
                this.http.post('api/Account/SaveToken', { userId: this.authService.userId(), deviceToken: this.token() }).subscribe()
            }
        })

        this.isIOS.set(this.checkIOS())
    }

    async requestPermission() {
        // If permission is already granted, then do nothing else.
        if (Notification.permission === 'granted') {
            this.requestToken()
            return
        }

        // Try to automatically prompt for permissions, which should work on Android, but not iOS.
        // For this to work on iOS, the user must have added the site to their home screen (PWA),
        // and clicked on a button or link to trigger this method.
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
            this.requestToken()
        } else {
            if (this.isIOS()) {
                this.services.notification.info(
                    'Allow Notifications',
                    'Please click the Request Notifications button on the Home page to enable notifications',
                )
                return
            } else {
                this.services.notification.info('Allow Notifications', 'Click the button next to the address bar to allow notifications')
            }
        }
    }

    requestToken() {
        this.afMessaging.requestToken.subscribe({
            next: token => {
                this.token.set(token)
            },
            error: error => {
                this.services.notification.warning(error, 'Token Request Failed')
            },
        })
    }

    // If the user is actively using the app, they will receive the notification in the currentMessage observable
    listenForMessages() {
        this.afMessaging.messages.subscribe(payload => {
            console.log('New notification received:', payload)
            this.notificationService.info(payload.data?.body ?? '', payload.data?.title, false, '/')
            this.currentMessage.next(payload)
        })
    }

    checkIOS(): boolean {
        return /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document)
    }
}
